import { US_STATES } from "@/shared/constants/constants";
import { isValidZipCode } from "@/shared/utils/validators";

export interface AddressParts {
  streetAddress: string;
  apartment: string;
  city: string;
  state: string;
  zipCode: string;
}

export const EMPTY_ADDRESS: AddressParts = {
  streetAddress: "",
  apartment: "",
  city: "",
  state: "",
  zipCode: "",
};

export function buildAddress(parts: AddressParts): string {
  const street = (parts.streetAddress ?? "").trim();
  const apt = (parts.apartment ?? "").trim();
  const city = (parts.city ?? "").trim();
  const state = (parts.state ?? "").trim();
  const zip = (parts.zipCode ?? "").trim();

  const segments = [street];
  if (apt) segments[0] += `, ${apt}`;
  if (city) segments.push(city);
  const stateZip = [state, zip].filter(Boolean).join(" ");
  if (stateZip) segments.push(stateZip);
  return segments.join(", ");
}

export function parseAddress(location: string): AddressParts {
  if (!location) return { ...EMPTY_ADDRESS };

  const stateNames: string[] = US_STATES.map((s) => s.label);
  const stateAbbreviations: string[] = US_STATES.map((s) => s.value);

  // Handle "city, state zip" format (e.g. "123 Main St, Austin, Texas 78701")
  const stateZipMatch = location.match(/,\s*([A-Za-z\s]+)\s+(\d{5}(?:-\d{4})?)$/);
  if (stateZipMatch) {
    const matchedState = stateZipMatch[1].trim();
    const isKnownState =
      stateNames.includes(matchedState) || stateAbbreviations.includes(matchedState);

    if (isKnownState) {
      const beforeStateZip = location.slice(0, stateZipMatch.index);
      const parts = beforeStateZip.split(",").map((p) => p.trim());

      return {
        streetAddress: parts[0] ?? "",
        apartment: parts.length > 2 ? parts.slice(1, -1).join(", ") : "",
        city: parts.length >= 2 ? parts[parts.length - 1] : "",
        state: matchedState,
        zipCode: stateZipMatch[2],
      };
    }
  }

  // Handle fully comma-separated format (e.g. "123 Main St, Austin, TX, 78701")
  // buildAddress produces: street, city, state, zip — parse by position from the end
  const parts = location.split(", ");
  const streetAddress = parts[0] ?? "";

  const remaining = parts.slice(1);
  let city = "";
  let state = "";
  let zipCode = "";

  // Identify zip (last part if numeric) and state (last or second-to-last)
  if (remaining.length > 0 && /^\d{5}(-\d{4})?$/.test(remaining[remaining.length - 1])) {
    zipCode = remaining.pop()!;
  }
  if (
    remaining.length > 0 &&
    (stateAbbreviations.includes(remaining[remaining.length - 1]) ||
      stateNames.includes(remaining[remaining.length - 1]))
  ) {
    state = remaining.pop()!;
  }
  if (remaining.length > 0) {
    city = remaining.join(", ");
  }

  return { streetAddress, apartment: "", city, state, zipCode };
}

export function isCityStateOnly(location: string): boolean {
  const parsed = parseAddress(location);
  return !parsed.zipCode && !parsed.city;
}

export const stripCountySuffix = (county: string): string =>
  county.replace(/\s+county$/i, "").trim();

export function toStateAbbreviation(value: string): string {
  if (!value) return "";
  const upper = value.trim().toUpperCase();
  const byAbbr = US_STATES.find((s) => s.value === upper);
  if (byAbbr) return byAbbr.value;
  const byName = US_STATES.find((s) => s.label.toUpperCase() === upper);
  return byName ? byName.value : value;
}

export function extractAddressFromPlace(
  rawValue: string,
  placeDetails?: google.maps.places.Place | null,
  subpremise?: string | null,
): AddressParts {
  if (!placeDetails?.addressComponents) {
    return { ...EMPTY_ADDRESS, streetAddress: rawValue, apartment: subpremise || "" };
  }

  const components = placeDetails.addressComponents;
  const streetNumber = components.find((c) => c.types.includes("street_number"))?.longText || "";
  const route = components.find((c) => c.types.includes("route"))?.longText || "";
  const streetAddress = [streetNumber, route].filter(Boolean).join(" ") || rawValue;

  const cityComponent =
    components.find((c) => c.types.includes("locality")) ||
    components.find((c) => c.types.includes("sublocality"));

  const stateComponent = components.find((c) => c.types.includes("administrative_area_level_1"));
  const zipComponent = components.find((c) => c.types.includes("postal_code"));

  return {
    streetAddress,
    apartment: subpremise || "",
    city: cityComponent?.longText || "",
    state: stateComponent?.longText || "",
    zipCode: zipComponent?.longText || "",
  };
}

export function validateAddress(
  value: AddressParts,
  name?: string,
): Partial<Record<keyof AddressParts, string>> | null {
  const errors: Partial<Record<keyof AddressParts, string>> = {};
  const whose = name ? `${name}'s` : "the";

  if (!value.streetAddress.trim()) {
    errors.streetAddress = `Please enter ${whose} last known address`;
  }
  if (!value.city.trim()) {
    errors.city = `Please enter the city for ${whose} address`;
  }
  if (!value.state.trim()) {
    errors.state = `Please select the state for ${whose} address`;
  }
  if (!value.zipCode.trim()) {
    errors.zipCode = `Please enter the zip code for ${whose} address`;
  } else if (!isValidZipCode(value.zipCode)) {
    errors.zipCode = "Please enter a valid 5-digit zip code";
  }

  return Object.keys(errors).length > 0 ? errors : null;
}

export function buildAddressFromMetadata(metadata: Record<string, unknown> | undefined): string {
  return buildAddress({
    streetAddress: (metadata?.street_address as string) ?? "",
    apartment: (metadata?.apartment as string) ?? "",
    city: (metadata?.city as string) ?? "",
    state: (metadata?.state as string) ?? "",
    zipCode: (metadata?.zip_code as string) ?? "",
  });
}
